import React from 'react';
import { Doughnut } from 'react-chartjs-2';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';
import { motion } from 'framer-motion';

ChartJS.register(ArcElement, Tooltip, Legend);

interface ServicesChartProps {
  data: {
    labels: string[];
    values: number[];
    revenues: number[];
  };
}

export function ServicesChart({ data }: ServicesChartProps) {
  const colors = [
    'rgba(99, 102, 241, 0.8)',
    'rgba(168, 85, 247, 0.8)',
    'rgba(236, 72, 153, 0.8)',
    'rgba(251, 146, 60, 0.8)',
    'rgba(34, 197, 94, 0.8)',
  ];

  const chartData = {
    labels: data.labels,
    datasets: [
      {
        label: 'מספר תורים',
        data: data.values,
        backgroundColor: colors,
        borderColor: colors.map(c => c.replace('0.8', '1')),
        borderWidth: 2,
        hoverOffset: 8,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
        position: 'bottom' as const,
        labels: {
          padding: 15,
          usePointStyle: true,
          font: {
            size: 12,
            family: 'inherit',
          },
        },
      },
      tooltip: {
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        titleColor: '#1f2937',
        bodyColor: '#1f2937',
        borderColor: '#e5e7eb',
        borderWidth: 1,
        padding: 12,
        callbacks: {
          label: function(context: any) {
            const label = context.label || '';
            const value = context.parsed || 0;
            const revenue = data.revenues[context.dataIndex] || 0;
            const total = context.dataset.data.reduce((a: number, b: number) => a + b, 0);
            const percentage = Math.round((value / total) * 100);
            return [
              `${label}`,
              `תורים: ${value} (${percentage}%)`,
              `הכנסה: ${new Intl.NumberFormat('he-IL').format(revenue)}₪`
            ];
          }
        }
      },
    },
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.2 }}
      className="bg-white rounded-2xl p-4 sm:p-6 border border-gray-200 shadow-sm"
    >
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-4">
        <h3 className="text-base sm:text-lg font-semibold text-gray-900">חלוקת שירותים</h3>
        <div className="flex items-center gap-2 text-xs sm:text-sm text-gray-500">
          <span>החודש הנבחר</span>
        </div>
      </div>
      <div className="h-48 sm:h-64 flex items-center justify-center">
        <Doughnut data={chartData} options={options} />
      </div>
      <div className="mt-4 sm:mt-6 space-y-2">
        {data.labels.map((label, index) => (
          <div key={label} className="flex items-center justify-between text-sm">
            <div className="flex items-center gap-2">
              <div
                className="w-3 h-3 rounded-full"
                style={{ backgroundColor: colors[index] }}
              />
              <span className="text-gray-700">{label}</span>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-gray-900 font-semibold">{data.values[index]} תורים</span>
              <span className="text-indigo-600 font-semibold">
                {new Intl.NumberFormat('he-IL').format(data.revenues[index])}₪
              </span>
            </div>
          </div>
        ))}
      </div>
    </motion.div>
  );
}
